import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, Button, Skeleton } from '@rackd/ui';
import { IconPlus, IconBooks, IconCrown, IconStar } from '@tabler/icons-react';
import { useCatalog, CatalogCard } from '@/features/catalog';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

type TierFilter = 'all' | 'free' | 'power_rack';

const tierTabs: { value: TierFilter; label: string; icon: typeof IconBooks }[] = [
  { value: 'all', label: 'All Programs', icon: IconBooks },
  { value: 'power_rack', label: 'Power Rack', icon: IconCrown },
  { value: 'free', label: 'Free', icon: IconBooks },
];

export function CatalogPage() {
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const { programs, isLoading, error } = useCatalog({
    accessTier: tierFilter === 'all' ? undefined : tierFilter,
  });

  const displayedPrograms = showFeaturedOnly
    ? programs.filter((p) => p.isFeatured)
    : programs;

  const featuredCount = programs.filter((p) => p.isFeatured).length;

  return (
    <motion.div
      className="space-y-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]">Program Catalog</h1>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">
            Manage official Rackd programs ({programs.length} total, {featuredCount} featured)
          </p>
        </div>
        <Link to="/catalog/new">
          <Button>
            <IconPlus size={18} />
            Add Program
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between border-b border-[var(--border)] pb-4">
        <div className="flex items-center gap-2">
          {tierTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tierFilter === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setTierFilter(tab.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[var(--accent-primary)] text-white'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--background-tertiary)]'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            showFeaturedOnly
              ? 'bg-[var(--pr-gold)]/10 text-[var(--pr-gold)]'
              : 'text-[var(--text-secondary)] hover:bg-[var(--background-tertiary)]'
          }`}
        >
          <IconStar size={18} />
          Featured Only
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start gap-4">
                <Skeleton className="w-20 h-20 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="p-8 text-center">
          <p className="text-[var(--error)]">Error loading catalog</p>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">{error.message}</p>
        </Card>
      ) : displayedPrograms.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-[var(--background-tertiary)] rounded-full flex items-center justify-center mx-auto mb-4">
            <IconBooks size={32} className="text-[var(--text-tertiary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--text)] mb-1">
            {showFeaturedOnly ? 'No featured programs' : 'No programs in catalog'}
          </h3>
          <p className="text-sm text-[var(--text-tertiary)] mb-4">
            {showFeaturedOnly
              ? 'Star some programs to feature them.'
              : 'Add your first official program to the catalog.'}
          </p>
          {!showFeaturedOnly && (
            <Link to="/catalog/new">
              <Button>
                <IconPlus size={18} />
                Add Program
              </Button>
            </Link>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {displayedPrograms.map((program) => (
            <CatalogCard key={program.id} program={program} />
          ))}
        </div>
      )}
    </motion.div>
  );
}
